import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productosPlacards } from '../../data/productos';

const ProductPlacards = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [isWhatsappModalOpen, setIsWhatsappModalOpen] = useState(false);
  const [whatsappForm, setWhatsappForm] = useState({ 
    name: '', 
    product: '', 
    locality: '',
    pickupMethod: '', 
    paymentMethod: '' 
  });
  const [whatsappAnimation, setWhatsappAnimation] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const product = productosPlacards[productId];

  // Resetear índice de imagen cuando cambie el producto
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [productId]);

  const nextImage = () => {
    if (product.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const openWhatsappModal = () => {
    setIsWhatsappModalOpen(true);
    setWhatsappAnimation(true);
    setTimeout(() => setWhatsappAnimation(false), 300);
    // Pre-llenar el nombre del producto
    setWhatsappForm(prev => ({
      ...prev,
      product: product.name
    }));
  };

  const closeWhatsappModal = () => {
    setIsWhatsappModalOpen(false);
    setWhatsappForm({ name: '', product: '', locality: '', pickupMethod: '', paymentMethod: '' });
  };

  const handleWhatsappFormChange = (e) => {
    setWhatsappForm({
      ...whatsappForm,
      [e.target.name]: e.target.value
    });
  };

  // Función para detectar si es dispositivo móvil
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const openWhatsappChat = () => {
    const message = `Hola! Mi nombre es: ${whatsappForm.name}

Te hablo por el mueble: ${whatsappForm.product}

Localidad: ${whatsappForm.locality}

Medio de retiro: ${whatsappForm.pickupMethod}

El medio de pago que voy a utilizar será: ${whatsappForm.paymentMethod}`;
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = '5491139057006';
    
    // URL diferente para móvil y PC
    const whatsappUrl = isMobile() 
      ? `https://wa.me/${phoneNumber}?text=${encodedMessage}`
      : `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    closeWhatsappModal();
  };

  if (!product) {
    return (
      <div className="product-page">
        <div className="error-container">
          <h1 className="error-title">Producto no encontrado</h1>
          <p className="error-message">El producto que buscas no existe o ha sido removido.</p>
          <button onClick={() => navigate('/')} className="error-button">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-page">
      <button onClick={() => navigate('/')} className="back-button">
        <i className="fas fa-arrow-left"></i> Volver al inicio
      </button>
      
      <div className="product-container">
        <div className="product-grid">
          {/* Imagen del producto con carrusel */}
          <div className="product-image-container">
            {product.images && product.images.length > 0 ? (
              <div className="carousel-container">
                <img
                  src={product.images[currentImageIndex]}
                  alt={`${product.name} - Imagen ${currentImageIndex + 1}`}
                  className="product-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />

                {/* Flechas de navegación */}
                {product.images.length > 1 && (
                  <>
                    <button className="carousel-arrow carousel-prev" onClick={prevImage}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button className="carousel-arrow carousel-next" onClick={nextImage}>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </>
                )}

                {/* Indicadores de puntos */}
                {product.images.length > 1 && (
                  <div className="carousel-indicators">
                    {product.images.map((_, index) => (
                      <button
                        key={index}
                        className={`carousel-indicator ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => goToImage(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="image-placeholder">
                Imagen no disponible
              </div>
            )}
          </div>

          {/* Información del producto */}
          <div className="product-info">
            <h1 className="product-title">{product.name}</h1>
            <p className="product-price">{product.price}</p>
            <p className="product-description">{product.description}</p>
            
            <button 
              className={`whatsapp-consult-button ${whatsappAnimation ? 'floating' : ''}`}
              onClick={openWhatsappModal}
            >
              <i className="fab fa-whatsapp whatsapp-icon"></i>
              Consultar por WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* Modal de WhatsApp */}
      {isWhatsappModalOpen && (
        <div className="whatsapp-modal-overlay" onClick={closeWhatsappModal}>
          <div className="whatsapp-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="whatsapp-modal-close" onClick={closeWhatsappModal}>
              <i className="fas fa-times"></i>
            </button>
            <div className="whatsapp-modal-header">
              <i className="fab fa-whatsapp whatsapp-icon"></i>
              <h3>Consulta por WhatsApp</h3>
            </div>
            <form className="whatsapp-form" onSubmit={(e) => e.preventDefault()}>
              <div className="form-group">
                <label htmlFor="name">Hola! Mi nombre es:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={whatsappForm.name}
                  onChange={handleWhatsappFormChange}
                  className="form-input"
                  placeholder="Tu nombre completo"
                  required
                  autoComplete="off"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="product">Te hablo por el mueble:</label>
                <div className="product-display">
                  {whatsappForm.product}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="locality">Localidad:</label>
                <input
                  type="text"
                  id="locality"
                  name="locality"
                  value={whatsappForm.locality}
                  onChange={handleWhatsappFormChange}
                  className="form-input"
                  placeholder="Tu localidad"
                  required
                  autoComplete="off"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="pickupMethod">Medio de retiro:</label>
                <select
                  id="pickupMethod"
                  name="pickupMethod"
                  value={whatsappForm.pickupMethod}
                  onChange={handleWhatsappFormChange}
                  className="form-select"
                  required
                  autoComplete="off"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Envío">Envío</option>
                  <option value="Punto de entrega">Punto de entrega</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="paymentMethod">El medio de pago que voy a utilizar será:</label>
                <select
                  id="paymentMethod"
                  name="paymentMethod"
                  value={whatsappForm.paymentMethod}
                  onChange={handleWhatsappFormChange}
                  className="form-select"
                  required
                  autoComplete="off"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Tarjeta de credito">Tarjeta de credito</option>
                </select>
              </div>
              
              <button 
                type="button" 
                className="whatsapp-chat-button"
                onClick={openWhatsappChat}
                disabled={!whatsappForm.name || !whatsappForm.locality.trim() || !whatsappForm.pickupMethod || !whatsappForm.paymentMethod}
              >
                <i className="fab fa-whatsapp"></i>
                Ir al chat
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPlacards;
